import { CheckOutIdentity } from "../types";

// The <jyloCheckOut> custom XML part is written by the API when a checked-out draft
// is served, and stripped server-side at check in (a document opened via
// ms-word:ofe|u| is read-only, so the add-in cannot delete it itself). The part
// travels inside the file and survives Save As, copy, email and reopening on another
// machine -- so the add-in never asks and never guesses which document this is.
//
// The XML is identical in every Open XML format, and the API stamps .docx, .xlsx and
// .pptx from one code path (DraftCheckOutMetadataStamper). Only the API for reaching
// the parts differs per host -- Word.customXmlParts, Excel.customXmlParts,
// presentation.customXmlParts -- so the shape lives here and the plumbing lives in
// each adapter.

export const CHECK_OUT_STAMP_ROOT = "jyloCheckOut";

function extractTag(xml: string, tag: string): string | undefined {
  const match = xml.match(new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`));
  return match?.[1]?.trim();
}

/** True when this custom XML part is a check-out stamp at all. */
export function isCheckOutStamp(xml: string): boolean {
  return xml.includes(`<${CHECK_OUT_STAMP_ROOT}>`);
}

/**
 * Reads an identity out of a stamp, or null when the part is not a stamp or is
 * missing the identifiers that make it usable. A partial stamp is treated as no
 * stamp: acting on half an identity is worse than reporting none.
 */
export function parseCheckOutStamp(xml: string): CheckOutIdentity | null {
  if (!isCheckOutStamp(xml)) return null;

  const projectObjectIdentifier = extractTag(xml, "ProjectObjectIdentifier");
  const documentObjectIdentifier = extractTag(xml, "DocumentObjectIdentifier");
  const draftObjectIdentifier = extractTag(xml, "DraftObjectIdentifier");
  const baseVersionNumber = Number(extractTag(xml, "BaseVersionNumber"));

  if (!projectObjectIdentifier || !documentObjectIdentifier || !draftObjectIdentifier) {
    return null;
  }

  return {
    projectObjectIdentifier,
    documentObjectIdentifier,
    draftObjectIdentifier,
    baseVersionNumber: Number.isFinite(baseVersionNumber) ? baseVersionNumber : 0,
  };
}

export function buildCheckOutStamp(identity: CheckOutIdentity): string {
  return (
    `<${CHECK_OUT_STAMP_ROOT}>` +
    `<ProjectObjectIdentifier>${identity.projectObjectIdentifier}</ProjectObjectIdentifier>` +
    `<DocumentObjectIdentifier>${identity.documentObjectIdentifier}</DocumentObjectIdentifier>` +
    `<DraftObjectIdentifier>${identity.draftObjectIdentifier}</DraftObjectIdentifier>` +
    `<BaseVersionNumber>${identity.baseVersionNumber}</BaseVersionNumber>` +
    `</${CHECK_OUT_STAMP_ROOT}>`
  );
}
